import OneSignal from 'react-onesignal';

let isOneSignalInitialized = false;

export const initializeOneSignal = async () => {
  try {
    // Check if already initialized
    if (isOneSignalInitialized) {
      console.log('OneSignal already initialized');
      return true;
    }

    // Check if OneSignal is already present and initialized
    if (typeof window !== 'undefined' && (window as any).OneSignal && (window as any).OneSignal.initialized) {
      console.log('OneSignal already initialized in window');
      isOneSignalInitialized = true;
      return true;
    }

    console.log('Initializing OneSignal...');
    
    // Allow live domains and Lovable preview/localhost for testing
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isAllowed =
      hostname.endsWith('getgambgo.com') ||
      hostname.endsWith('lovableproject.com') ||
      hostname === 'localhost';
    if (!isAllowed) {
      console.warn('OneSignal disabled on unauthorized origin:', hostname);
      return false;
    }
    
    // Register service worker first
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', { 
          scope: '/',
          updateViaCache: 'none'
        });
        console.log('Service Worker registered successfully:', registration);
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.error('Service Worker registration failed:', swError);
      }
    }
    
    // Initialize OneSignal with proper configuration
    await OneSignal.init({
      appId: "8e92ff4c-e280-4669-b4fb-67199b78491f",
      allowLocalhostAsSecureOrigin: true,
      serviceWorkerPath: '/sw.js',
      serviceWorkerParam: { scope: '/' },
      requiresUserPrivacyConsent: false,
      autoRegister: false // We handle SW registration manually
    });

    console.log('OneSignal initialization completed');
    isOneSignalInitialized = true;
    
    return true;
  } catch (error) {
    console.error('Error initializing OneSignal:', error);
    const errorMsg = error?.message || '';
    
    // Handle already initialized error
    if (errorMsg.includes('SDK already initialized')) {
      console.log('OneSignal SDK already initialized, marking as ready');
      isOneSignalInitialized = true;
      return true;
    }
    
    // Domain restriction error - needs OneSignal dashboard configuration
    if (errorMsg.includes('Can only be used on')) {
      console.error('⚠️ DOMAIN NOT ALLOWED: You need to add this domain to OneSignal dashboard');
      console.error('Go to OneSignal Dashboard > Settings > Allowed Origins and add:', window.location.origin);
      return false;
    }
    
    return false;
  }
};

export const subscribeUser = async () => {
  try {
    console.log('Starting user subscription process...');
    
    // Ensure OneSignal is initialized first
    if (!isOneSignalInitialized) {
      console.log('OneSignal not initialized, initializing now...');
      const initSuccess = await initializeOneSignal();
      if (!initSuccess) {
        throw new Error('Failed to initialize OneSignal');
      }
    }

    // Request permission using OneSignal's Notifications API with timeout
    console.log('Requesting notification permission...');
    const permissionPromise = OneSignal.Notifications.requestPermission();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Permission request timeout')), 10000)
    );
    
    try {
      await Promise.race([permissionPromise, timeoutPromise]);
    } catch (error) {
      console.error('Permission request failed or timed out:', error);
      throw error;
    }
    
    // Check browser permission status
    const browserPermission = Notification.permission;
    console.log('Browser permission:', browserPermission);
    
    if (browserPermission !== 'granted') {
      return { 
        isSubscribed: false, 
        playerId: null,
        permission: 'denied' 
      };
    }
    
    // Get subscription info with limited retries and timeout
    let attempts = 0;
    const maxAttempts = 3;
    let isSubscribed = false;
    let playerId = null;
    
    while (attempts < maxAttempts) {
      attempts++;
      console.log(`Checking subscription status (attempt ${attempts}/${maxAttempts})...`);
      
      try {
        isSubscribed = OneSignal.User.PushSubscription.optedIn;
        playerId = OneSignal.User.PushSubscription.id;
        
        console.log('Subscription check result:', { isSubscribed, playerId });
        
        if (isSubscribed && playerId) {
          break;
        }
        
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (err) {
        console.log('Error checking subscription status:', err);
        break; // Don't retry on error
      }
    }
    
    return { 
      isSubscribed: isSubscribed || false, 
      playerId: playerId || null,
      permission: browserPermission === 'granted' ? 'granted' : 'denied' 
    };
  } catch (error) {
    console.error('Error subscribing user:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { 
      isSubscribed: false, 
      error: errorMessage, 
      permission: 'denied' 
    };
  }
};

export const sendNotification = async (message: string, heading: string, userIds?: string[]) => {
  try {
    // This would typically be called from your backend
    // Here's the structure for the API call
    const notificationData = {
      app_id: "8e92ff4c-e280-4669-b4fb-67199b78491f",
      headings: { en: heading },
      contents: { en: message },
      ...(userIds ? { include_player_ids: userIds } : { included_segments: ["Subscribed Users"] }),
    };
    
    console.log('Notification data prepared:', notificationData);
    // You'll need to implement the actual API call in your backend
    return notificationData;
  } catch (error) {
    console.error('Error preparing notification:', error);
    return null;
  }
};

export const setUserTags = async (tags: Record<string, string>) => {
  try {
    OneSignal.User?.addTags?.(tags);
    console.log('User tags set:', tags);
    return true;
  } catch (error) {
    console.error('Error setting user tags:', error);
    return false;
  }
};

export const getSubscriptionStatus = async () => {
  try {
    const isSubscribed = OneSignal.User.PushSubscription.optedIn;
    const playerId = OneSignal.User.PushSubscription.id;
    
    return {
      isSubscribed: isSubscribed || false,
      permission: isSubscribed ? 'granted' : 'default',
      playerId: playerId || null
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return {
      isSubscribed: false,
      permission: 'default',
      playerId: null
    };
  }
};